// Planner preferences: the user-facing toggles, their defaults, and the
// derived weights the itinerary engine consumes. See
// docs/features/day-planner-plan.md section 7.3.

import type { AreaSport, Difficulty } from "./graph-types";
import { coerceSkiPass, type SkiPassChoice } from "./ski-pass";
import {
  DAY_STYLES,
  applyDayStyleForCoercion,
  defaultDayStyleForSport,
  isValidDayStyle,
  type DayStyle,
  type DayStyleConfig,
} from "./day-styles";
import {
  climbBudgetM as mtbClimbBudgetM,
  type BikeType,
  type ClimbingTolerance,
  type RidingStyle,
} from "./mtb-engine";
import {
  ALL_LIFT_GROUPS_ALLOWED,
  bannedLiftTypes,
  type AllowedLiftTypes,
} from "./lift-preferences";

// The six layerable day-vibe objective weights. Each defaults to 0 (off, exact
// engine identity) and is set to a tuned positive value when its vibe toggle is
// on. The engine reads prefs.objectives without a sport guard, so both arms of
// the union carry the same record shape.
export type PlannerObjectives = {
  sun: number;
  scenic: number;
  firstTracks: number;
  coverage: number;
  cruise: number;
  quiet: number;
};

export const EMPTY_OBJECTIVES: PlannerObjectives = {
  sun: 0,
  scenic: 0,
  firstTracks: 0,
  coverage: 0,
  cruise: 0,
  quiet: 0,
};

const OBJECTIVE_WEIGHT_MIN = 0;
const OBJECTIVE_WEIGHT_MAX = 1.5;

// Ski preferences: the original day-planner toggle set. Now one arm of the
// sport-discriminated PlannerPreferences union, tagged sport: "ski".
export type SkiPreferences = {
  skiPass?: SkiPassChoice;
  sport: "ski";
  dayStyle: DayStyle;
  date: "today" | "tomorrow";
  startTime: string; // 'HH:MM'
  endTime: string; // 'HH:MM'
  ability: "beginner" | "intermediate" | "advanced" | "expert";
  difficultyMode: "comfort" | "push" | "challenge";
  distance: "short" | "half-day" | "full-day" | "max";
  liftPrefs: {
    avoidDrags: boolean;
    preferGondolas: boolean;
    allowedTypes?: AllowedLiftTypes;
    preferFastLifts?: boolean;
  };
  lunch: {
    time: string;
    style: "packed" | "cheap" | "sit-down" | "skip";
    price: "budget" | "mid" | "treat";
  };
  coffeeStops: "morning" | "afternoon" | "both" | "skip";
  apres: "party" | "quiet" | "coffee-cake" | "skip";
  group: "solo" | "couple" | "family" | "mates";
  returnToStart: boolean;
  finishPoint?: string;
  requiredStop?: { nodeId: string; time: string; pauseMinutes: number };
  excludedLiftIds?: string[];
  includeUnknownLifts: boolean;
  startPoint: string | "auto";
  allowBus: boolean;
  poling: "fine" | "minimise";
  treeRuns: "prefer" | "avoid" | "no-preference";
  objectives: PlannerObjectives;
};

// MTB preferences: the mountain-bike day-planner toggle set. The lunch / coffee
// / apres / group controls are reused verbatim from the ski shape (Decision 7);
// the sport-specific axes are riding style, fitness, climbing tolerance and bike
// type. allowBus is carried on both shapes so compose.ts / itinerary.ts can read
// prefs.allowBus and prefs.includeUnknownLifts without a sport guard.
export type MtbPreferences = {
  sport: "mtb";
  dayStyle: DayStyle;
  date: "today" | "tomorrow";
  startTime: string;
  endTime: string;
  ability: "beginner" | "intermediate" | "advanced" | "expert";
  difficultyMode: "comfort" | "push" | "challenge";
  ridingStyle: RidingStyle;
  fitness: "low" | "moderate" | "high" | "very-high";
  climbingTolerance: ClimbingTolerance;
  // Whether the rider has (and wants to use) a lift pass. "pedal-only" plans a
  // pure human-powered day: no uplifts, every descent earned by pedalling. The
  // honest mode for shoulder season (lifts not yet open), no-pass riders and
  // XC/e-bike loops. "lift-pass" is the default and preserves prior behaviour.
  liftAccess: "lift-pass" | "pedal-only";
  distance: "short" | "half-day" | "full-day" | "max";
  bikeType: BikeType;
  lunch: SkiPreferences["lunch"];
  coffeeStops: SkiPreferences["coffeeStops"];
  apres: SkiPreferences["apres"];
  group: SkiPreferences["group"];
  returnToStart: boolean;
  finishPoint?: string;
  requiredStop?: { nodeId: string; time: string; pauseMinutes: number };
  excludedLiftIds?: string[];
  startPoint: string | "auto";
  includeUnknownLifts: boolean;
  allowBus: boolean;
  objectives: PlannerObjectives;
};

export type PlannerPreferences = SkiPreferences | MtbPreferences;

export const DEFAULT_PREFERENCES: SkiPreferences = {
  sport: "ski",
  dayStyle: "locals-best",
  date: "today",
  startTime: "09:00",
  endTime: "16:00",
  ability: "intermediate",
  difficultyMode: "comfort",
  distance: "full-day",
  liftPrefs: {
    avoidDrags: false,
    preferGondolas: false,
    preferFastLifts: false,
  },
  lunch: { time: "12:30", style: "cheap", price: "mid" },
  coffeeStops: "skip",
  apres: "skip",
  group: "couple",
  returnToStart: true,
  excludedLiftIds: [],
  includeUnknownLifts: true,
  startPoint: "auto",
  allowBus: false,
  poling: "fine",
  treeRuns: "no-preference",
  objectives: { ...EMPTY_OBJECTIVES },
};

export const DEFAULT_MTB_PREFERENCES: MtbPreferences = {
  sport: "mtb",
  dayStyle: "locals-best-ride",
  date: "today",
  startTime: "09:00",
  endTime: "16:00",
  ability: "intermediate",
  difficultyMode: "comfort",
  ridingStyle: "flow",
  fitness: "moderate",
  climbingTolerance: "some-pedalling",
  liftAccess: "lift-pass",
  distance: "full-day",
  bikeType: "full-suspension",
  lunch: { time: "12:30", style: "cheap", price: "mid" },
  coffeeStops: "skip",
  apres: "skip",
  group: "couple",
  returnToStart: true,
  excludedLiftIds: [],
  startPoint: "auto",
  includeUnknownLifts: true,
  allowBus: false,
  objectives: { ...EMPTY_OBJECTIVES },
};

const DATE_OPTIONS = ["today", "tomorrow"] as const satisfies readonly SkiPreferences["date"][];
const ABILITY_OPTIONS = ["beginner", "intermediate", "advanced", "expert"] as const satisfies readonly PlannerPreferences["ability"][];
const DIFFICULTY_MODE_OPTIONS = ["comfort", "push", "challenge"] as const satisfies readonly PlannerPreferences["difficultyMode"][];
const DISTANCE_OPTIONS = ["short", "half-day", "full-day", "max"] as const satisfies readonly PlannerPreferences["distance"][];
const LUNCH_STYLE_OPTIONS = ["packed", "cheap", "sit-down", "skip"] as const satisfies readonly SkiPreferences["lunch"]["style"][];
const LUNCH_PRICE_OPTIONS = ["budget", "mid", "treat"] as const satisfies readonly SkiPreferences["lunch"]["price"][];
const COFFEE_STOP_OPTIONS = ["morning", "afternoon", "both", "skip"] as const satisfies readonly PlannerPreferences["coffeeStops"][];
const APRES_OPTIONS = ["party", "quiet", "coffee-cake", "skip"] as const satisfies readonly PlannerPreferences["apres"][];
const GROUP_OPTIONS = ["solo", "couple", "family", "mates"] as const satisfies readonly PlannerPreferences["group"][];
const POLING_OPTIONS = ["fine", "minimise"] as const satisfies readonly SkiPreferences["poling"][];
const TREE_RUN_OPTIONS = ["prefer", "avoid", "no-preference"] as const satisfies readonly SkiPreferences["treeRuns"][];
const RIDING_STYLE_OPTIONS = [
  "flow",
  "technical-enduro",
  "downhill-park",
  "cross-country",
  "e-bike-friendly",
] as const satisfies readonly MtbPreferences["ridingStyle"][];
const FITNESS_OPTIONS = ["low", "moderate", "high", "very-high"] as const satisfies readonly MtbPreferences["fitness"][];
const CLIMBING_TOLERANCE_OPTIONS = [
  "lift-only",
  "some-pedalling",
  "happy-to-climb",
  "maximise",
] as const satisfies readonly MtbPreferences["climbingTolerance"][];
const LIFT_ACCESS_OPTIONS = ["lift-pass", "pedal-only"] as const satisfies readonly MtbPreferences["liftAccess"][];
const BIKE_TYPE_OPTIONS = ["hardtail", "full-suspension", "e-bike", "dh"] as const satisfies readonly MtbPreferences["bikeType"][];

/**
 * Merge an untrusted preferences payload over the per-sport defaults (shallow,
 * with the nested objects merged explicitly). Shared by the itinerary and edit
 * routes so both coerce the body identically. The `sport` argument selects the
 * arm of the union; the raw payload's own `sport` (if any) is ignored, so a
 * caller cannot smuggle the wrong shape past the discriminant.
 */
// Merge an untrusted objectives payload over the zero default. Non-object input
// (string, null, array) collapses to all-zero, so a malformed body can never
// smuggle a non-numeric weight into the engine.
function coerceObjectives(raw: unknown): PlannerObjectives {
  const obj = raw && typeof raw === "object" && !Array.isArray(raw) ? (raw as Partial<PlannerObjectives>) : {};
  const num = (v: unknown): number => {
    if (typeof v !== "number" || !Number.isFinite(v)) return 0;
    return Math.max(OBJECTIVE_WEIGHT_MIN, Math.min(OBJECTIVE_WEIGHT_MAX, v));
  };
  return {
    sun: num(obj.sun),
    scenic: num(obj.scenic),
    firstTracks: num(obj.firstTracks),
    coverage: num(obj.coverage),
    cruise: num(obj.cruise),
    quiet: num(obj.quiet),
  };
}

function recordOrEmpty(raw: unknown): Record<string, unknown> {
  return raw && typeof raw === "object" && !Array.isArray(raw)
    ? raw as Record<string, unknown>
    : {};
}

function enumOr<T extends string>(
  value: unknown,
  allowed: readonly T[],
  fallback: T,
): T {
  if (typeof value !== "string") return fallback;
  return (allowed as readonly string[]).includes(value) ? value as T : fallback;
}

function boolOr(value: unknown, fallback: boolean): boolean {
  return typeof value === "boolean" ? value : fallback;
}

function stringOr(value: unknown, fallback: string): string {
  return typeof value === "string" ? value : fallback;
}

/** Preserve an incomplete selection so the planner reports it instead of dropping it. */
function coerceRequiredStop(raw: unknown): PlannerPreferences["requiredStop"] {
  if (raw == null) return undefined;
  const value = recordOrEmpty(raw);
  return {
    nodeId: typeof value.nodeId === "string" ? value.nodeId.slice(0, 200) : "",
    time: typeof value.time === "string" ? value.time.slice(0, 20) : "",
    pauseMinutes: typeof value.pauseMinutes === "number" && Number.isInteger(value.pauseMinutes) ? value.pauseMinutes : -1,
  };
}

export function coercePreferences(
  raw: unknown,
  sport: AreaSport = "ski",
  touchedFields?: readonly string[],
): PlannerPreferences {
  const obj = recordOrEmpty(raw);
  if (sport === "mtb") {
    const lunch = recordOrEmpty(obj.lunch);
    const dayStyle =
      typeof obj.dayStyle === "string" && isValidDayStyle("mtb", obj.dayStyle)
        ? obj.dayStyle
        : defaultDayStyleForSport("mtb");
    const base: PlannerPreferences = {
      ...DEFAULT_MTB_PREFERENCES,
      sport: "mtb",
      dayStyle,
      date: enumOr(obj.date, DATE_OPTIONS, DEFAULT_MTB_PREFERENCES.date),
      startTime: stringOr(obj.startTime, DEFAULT_MTB_PREFERENCES.startTime),
      endTime: stringOr(obj.endTime, DEFAULT_MTB_PREFERENCES.endTime),
      ability: enumOr(obj.ability, ABILITY_OPTIONS, DEFAULT_MTB_PREFERENCES.ability),
      difficultyMode: enumOr(obj.difficultyMode, DIFFICULTY_MODE_OPTIONS, DEFAULT_MTB_PREFERENCES.difficultyMode),
      ridingStyle: enumOr(obj.ridingStyle, RIDING_STYLE_OPTIONS, DEFAULT_MTB_PREFERENCES.ridingStyle),
      fitness: enumOr(obj.fitness, FITNESS_OPTIONS, DEFAULT_MTB_PREFERENCES.fitness),
      climbingTolerance: enumOr(
        obj.climbingTolerance,
        CLIMBING_TOLERANCE_OPTIONS,
        DEFAULT_MTB_PREFERENCES.climbingTolerance,
      ),
      liftAccess: enumOr(obj.liftAccess, LIFT_ACCESS_OPTIONS, DEFAULT_MTB_PREFERENCES.liftAccess),
      distance: enumOr(obj.distance, DISTANCE_OPTIONS, DEFAULT_MTB_PREFERENCES.distance),
      bikeType: enumOr(obj.bikeType, BIKE_TYPE_OPTIONS, DEFAULT_MTB_PREFERENCES.bikeType),
      lunch: {
        time: stringOr(lunch.time, DEFAULT_MTB_PREFERENCES.lunch.time),
        style: enumOr(lunch.style, LUNCH_STYLE_OPTIONS, DEFAULT_MTB_PREFERENCES.lunch.style),
        price: enumOr(lunch.price, LUNCH_PRICE_OPTIONS, DEFAULT_MTB_PREFERENCES.lunch.price),
      },
      coffeeStops: enumOr(obj.coffeeStops, COFFEE_STOP_OPTIONS, DEFAULT_MTB_PREFERENCES.coffeeStops),
      apres: enumOr(obj.apres, APRES_OPTIONS, DEFAULT_MTB_PREFERENCES.apres),
      group: enumOr(obj.group, GROUP_OPTIONS, DEFAULT_MTB_PREFERENCES.group),
      returnToStart: boolOr(obj.returnToStart, DEFAULT_MTB_PREFERENCES.returnToStart),
      excludedLiftIds: Array.isArray(obj.excludedLiftIds) ? obj.excludedLiftIds.filter((id): id is string => typeof id === "string" && id.length <= 200).slice(0, 30) : [],
      requiredStop: coerceRequiredStop(obj.requiredStop),
      finishPoint: typeof obj.finishPoint === "string" ? obj.finishPoint.slice(0, 200) : undefined,
      startPoint: stringOr(obj.startPoint, DEFAULT_MTB_PREFERENCES.startPoint),
      includeUnknownLifts: boolOr(obj.includeUnknownLifts, DEFAULT_MTB_PREFERENCES.includeUnknownLifts),
      allowBus: boolOr(obj.allowBus, DEFAULT_MTB_PREFERENCES.allowBus),
      objectives: coerceObjectives(obj.objectives),
    };
    return applyDayStyleForCoercion(base, dayStyle, touchedFields, DEFAULT_MTB_PREFERENCES);
  }
  const liftPrefs = recordOrEmpty(obj.liftPrefs);
  const allowedTypesRaw = recordOrEmpty(liftPrefs.allowedTypes);
  const hasAllowedTypesInput = (
    Object.keys(ALL_LIFT_GROUPS_ALLOWED) as (keyof AllowedLiftTypes)[]
  ).some((group) => typeof allowedTypesRaw[group] === "boolean");
  const resolvedAllowedTypes: AllowedLiftTypes | undefined = hasAllowedTypesInput
    ? {
        drags: boolOr(allowedTypesRaw.drags, true),
        gondolas: boolOr(allowedTypesRaw.gondolas, true),
        chairs: boolOr(allowedTypesRaw.chairs, true),
      }
    : undefined;
  const allowedTypes = resolvedAllowedTypes && Object.values(resolvedAllowedTypes).some(Boolean)
    ? resolvedAllowedTypes
    : undefined;
  const lunch = recordOrEmpty(obj.lunch);
  const dayStyle =
    typeof obj.dayStyle === "string" && isValidDayStyle("ski", obj.dayStyle)
      ? obj.dayStyle
      : defaultDayStyleForSport("ski");
  const base: PlannerPreferences = {
    ...DEFAULT_PREFERENCES,
    sport: "ski",
    dayStyle,
    skiPass: coerceSkiPass(obj.skiPass),
    date: enumOr(obj.date, DATE_OPTIONS, DEFAULT_PREFERENCES.date),
    startTime: stringOr(obj.startTime, DEFAULT_PREFERENCES.startTime),
    endTime: stringOr(obj.endTime, DEFAULT_PREFERENCES.endTime),
    ability: enumOr(obj.ability, ABILITY_OPTIONS, DEFAULT_PREFERENCES.ability),
    difficultyMode: enumOr(obj.difficultyMode, DIFFICULTY_MODE_OPTIONS, DEFAULT_PREFERENCES.difficultyMode),
    distance: enumOr(obj.distance, DISTANCE_OPTIONS, DEFAULT_PREFERENCES.distance),
    liftPrefs: {
      avoidDrags: boolOr(liftPrefs.avoidDrags, DEFAULT_PREFERENCES.liftPrefs.avoidDrags),
      preferGondolas: boolOr(liftPrefs.preferGondolas, DEFAULT_PREFERENCES.liftPrefs.preferGondolas),
      ...(allowedTypes ? { allowedTypes } : {}),
      preferFastLifts: boolOr(
        liftPrefs.preferFastLifts,
        DEFAULT_PREFERENCES.liftPrefs.preferFastLifts ?? false,
      ),
    },
    lunch: {
      time: stringOr(lunch.time, DEFAULT_PREFERENCES.lunch.time),
      style: enumOr(lunch.style, LUNCH_STYLE_OPTIONS, DEFAULT_PREFERENCES.lunch.style),
      price: enumOr(lunch.price, LUNCH_PRICE_OPTIONS, DEFAULT_PREFERENCES.lunch.price),
    },
    coffeeStops: enumOr(obj.coffeeStops, COFFEE_STOP_OPTIONS, DEFAULT_PREFERENCES.coffeeStops),
    apres: enumOr(obj.apres, APRES_OPTIONS, DEFAULT_PREFERENCES.apres),
    group: enumOr(obj.group, GROUP_OPTIONS, DEFAULT_PREFERENCES.group),
    returnToStart: boolOr(obj.returnToStart, DEFAULT_PREFERENCES.returnToStart),
    excludedLiftIds: Array.isArray(obj.excludedLiftIds) ? obj.excludedLiftIds.filter((id): id is string => typeof id === "string" && id.length <= 200).slice(0, 30) : [],
      requiredStop: coerceRequiredStop(obj.requiredStop),
      finishPoint: typeof obj.finishPoint === "string" ? obj.finishPoint.slice(0, 200) : undefined,
    includeUnknownLifts: boolOr(obj.includeUnknownLifts, DEFAULT_PREFERENCES.includeUnknownLifts),
    startPoint: stringOr(obj.startPoint, DEFAULT_PREFERENCES.startPoint),
    allowBus: boolOr(obj.allowBus, DEFAULT_PREFERENCES.allowBus),
    poling: enumOr(obj.poling, POLING_OPTIONS, DEFAULT_PREFERENCES.poling),
    treeRuns: enumOr(obj.treeRuns, TREE_RUN_OPTIONS, DEFAULT_PREFERENCES.treeRuns),
    objectives: coerceObjectives(obj.objectives),
  };
  return applyDayStyleForCoercion(base, dayStyle, touchedFields, DEFAULT_PREFERENCES);
}

// ---------------------------------------------------------------------------
// Difficulty ranking
// ---------------------------------------------------------------------------

export const DIFFICULTY_RANK: Record<Difficulty, number> = {
  novice: 0,
  easy: 1,
  intermediate: 2,
  advanced: 3,
  expert: 4,
  freeride: 4, // ungraded steep terrain: treat as top tier
};

const ABILITY_CEILING: Record<PlannerPreferences["ability"], number> = {
  beginner: 1, // blue
  intermediate: 2, // red
  advanced: 3, // black
  expert: 4, // double black / freeride
};

const ABILITY_TIME_FACTOR: Record<PlannerPreferences["ability"], number> = {
  beginner: 1.6,
  intermediate: 1.0,
  advanced: 0.8,
  expert: 0.7,
};

const GROUP_TIME_FACTOR: Record<PlannerPreferences["group"], number> = {
  solo: 1.0,
  couple: 1.0,
  family: 1.3,
  mates: 1.1,
};

const DISTANCE_KM: Record<PlannerPreferences["distance"], number> = {
  short: 12,
  "half-day": 20,
  "full-day": 32,
  max: 45,
};

const ABILITY_DISTANCE_FACTOR: Record<PlannerPreferences["ability"], number> = {
  beginner: 0.7,
  intermediate: 1.0,
  advanced: 1.2,
  expert: 1.35,
};

// Push mode lets at most this many runs exceed the comfort ceiling by one step.
export const PUSH_BUDGET = 2;

// ---------------------------------------------------------------------------
// MTB-only derive tables
// ---------------------------------------------------------------------------

// How hard a rider has to work to climb, by stated fitness. >1 means slower
// (lower fitness costs more time per climb edge); <1 means faster.
const MTB_FITNESS_FACTOR: Record<MtbPreferences["fitness"], number> = {
  low: 1.5,
  moderate: 1.0,
  high: 0.8,
  "very-high": 0.65,
};

// Bike-type effect on climbing time. A hardtail is a touch quicker uphill, a DH
// rig is heavy and slow. e-bike is handled separately (a flat 0.5, fitness-free).
const MTB_BIKE_CLIMB_MULT: Record<
  Exclude<MtbPreferences["bikeType"], "e-bike">,
  number
> = {
  hardtail: 0.9,
  "full-suspension": 1.0,
  dh: 1.3,
};

// The e-bike climbFactor is a flat assist that ignores rider fitness.
const MTB_EBIKE_CLIMB_FACTOR = 0.5;

// Target ride distance (km) by the MTB distance preset, before the ability
// distance factor is applied (riders cover more ground than skiers in a day).
const MTB_DISTANCE_KM: Record<MtbPreferences["distance"], number> = {
  short: 15,
  "half-day": 28,
  "full-day": 45,
  max: 70,
};

export type DerivedPreferences = {
  /** Highest difficulty rank allowed at all (push uses the budget for ceiling+1). */
  maxRank: number;
  /** Ceiling+1 runs are budget-limited (push) or free (challenge). */
  ceilingRank: number;
  pushBudget: number;
  timeFactor: number; // ability x group
  targetKm: number;
  startMinutes: number;
  endMinutes: number;
  lunchMinutes: number;
  /** Which sport this plan is for (ski-only factors gate on this downstream). */
  sport: AreaSport;
  /** Climb-edge time multiplier (ski = 1; MTB = fitness x bike-type, or 0.5 for e-bike). */
  climbFactor: number;
  /** Total metres of pedal-up ascent allowed in a day (ski = 0; MTB by tolerance). */
  climbBudgetM: number;
  /** MTB "no lift pass" mode: the engine gate refuses every lift edge and the
   * climb budget is lifted so the day is bounded by time and fitness, not by a
   * climbing-tolerance cap. Always false for ski. */
  pedalOnly: boolean;
  /** Resolved day-style objective and copy config for the pure engine. */
  styleConfig: DayStyleConfig;
  /** Concrete known lift types the skier has banned. Empty for MTB and defaults. */
  bannedLiftTypes: ReadonlySet<string>;
};

// In pedal-only mode the rider climbs everything, so the per-day ascent budget
// must not be the limiter (time and the fitness-scaled climb factor are). This
// floor is high enough that a full day of pedalling is never budget-capped, yet
// still finite as a runaway backstop. A rider who explicitly wants to maximise
// climbing keeps their larger budget.
const PEDAL_ONLY_CLIMB_FLOOR_M = 4000;

// The MTB climb-edge time multiplier: fitness x bike-type, except an e-bike is
// a flat assist that ignores fitness. Pulled out so the calibration lives in one
// place and the derive function stays readable.
function mtbClimbFactor(prefs: MtbPreferences): number {
  if (prefs.bikeType === "e-bike") return MTB_EBIKE_CLIMB_FACTOR;
  return MTB_FITNESS_FACTOR[prefs.fitness] * MTB_BIKE_CLIMB_MULT[prefs.bikeType];
}

function parseHm(hm: string): number {
  const [h, m] = hm.split(":").map(Number);
  return (h || 0) * 60 + (m || 0);
}

export function derivePreferences(prefs: PlannerPreferences): DerivedPreferences {
  const ceilingRank = ABILITY_CEILING[prefs.ability];
  let maxRank: number;
  let pushBudget = 0;
  if (prefs.difficultyMode === "comfort") {
    maxRank = ceilingRank;
  } else if (prefs.difficultyMode === "push") {
    maxRank = Math.min(4, ceilingRank + 1);
    pushBudget = PUSH_BUDGET;
  } else {
    // challenge: +1 step freely (still capped at the top tier)
    maxRank = Math.min(4, ceilingRank + 1);
    pushBudget = Number.POSITIVE_INFINITY;
  }
  // Distance, climb factor and climb budget are sport-specific. Ski values are
  // computed exactly as before; MTB swaps in its own distance presets and the
  // fitness/bike climb maths (the ski branch keeps climbFactor 1, budget 0).
  const abilityDistance = ABILITY_DISTANCE_FACTOR[prefs.ability];
  const targetKm =
    prefs.sport === "mtb"
      ? MTB_DISTANCE_KM[prefs.distance] * abilityDistance
      : DISTANCE_KM[prefs.distance] * abilityDistance;
  const climbFactor = prefs.sport === "mtb" ? mtbClimbFactor(prefs) : 1;
  const pedalOnly = prefs.sport === "mtb" && prefs.liftAccess === "pedal-only";
  // Pedal-only lifts the ascent budget to a generous floor (the day is bounded
  // by time and fitness, not a tolerance cap), which also resolves the
  // contradiction of a "lift-only" tolerance with no lift pass. Otherwise the
  // budget follows the stated climbing tolerance, as before (ski = 0).
  const climbBudget =
    prefs.sport === "mtb"
      ? pedalOnly
        ? Math.max(mtbClimbBudgetM(prefs.climbingTolerance), PEDAL_ONLY_CLIMB_FLOOR_M)
        : mtbClimbBudgetM(prefs.climbingTolerance)
      : 0;

  return {
    maxRank,
    ceilingRank,
    pushBudget,
    timeFactor: ABILITY_TIME_FACTOR[prefs.ability] * GROUP_TIME_FACTOR[prefs.group],
    targetKm,
    startMinutes: parseHm(prefs.startTime),
    endMinutes: parseHm(prefs.endTime),
    lunchMinutes: parseHm(prefs.lunch.time),
    sport: prefs.sport,
    climbFactor,
    climbBudgetM: climbBudget,
    pedalOnly,
    styleConfig: DAY_STYLES[prefs.dayStyle] ?? DAY_STYLES[defaultDayStyleForSport(prefs.sport)],
    bannedLiftTypes:
      prefs.sport === "ski"
        ? bannedLiftTypes(prefs.liftPrefs.allowedTypes)
        : new Set<string>(),
  };
}

export function rankToDifficultyLabel(rank: number): string {
  if (rank <= 0) return "green";
  if (rank === 1) return "blue";
  if (rank === 2) return "red";
  if (rank === 3) return "black";
  return "expert";
}
